/**
 * Admin API Utility (SEC-002 hardened, 2026-07-01)
 *
 * All admin endpoints now authenticate via the HttpOnly session cookie set
 * by /api/auth/login. There is NO admin API key shipped in the browser
 * bundle. If the user is not signed in as admin, the server returns 403 and
 * we surface that upstream — never bypass with a key.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Generic admin API call wrapper — always sends credentials (HttpOnly cookie).
 */
export const adminFetch = async (endpoint, options = {}) => {
  const url = `${BACKEND_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/** Migration operations */
export const runTutorVisibilityMigration = async () =>
  adminFetch('/api/admin/migrate/tutor-visibility', { method: 'POST' });
export const checkMigrationStatus = async () =>
  adminFetch('/api/admin/migrate/status', { method: 'GET' });
export const seedSampleTutors = async () =>
  adminFetch('/api/admin/seed/tutors', { method: 'POST' });

/** Student Management */
export const fetchStudents = async (filters = {}) => {
  const qs = new URLSearchParams();
  if (filters.search) qs.append('search', filters.search);
  if (filters.status) qs.append('status', filters.status);
  const q = qs.toString();
  return adminFetch(`/api/admin/student-enrollments${q ? `?${q}` : ''}`, { method: 'GET' });
};
export const fetchStudentById = async (studentId) =>
  adminFetch(`/api/admin/student-enrollments/${studentId}`, { method: 'GET' });
export const updateStudentStatus = async (studentId, status) =>
  adminFetch(`/api/admin/student-enrollments/${studentId}/status`, {
    method: 'PATCH', body: JSON.stringify({ status }),
  });
export const assignTutorToStudent = async (studentId, tutorId) =>
  adminFetch(`/api/admin/student-enrollments/${studentId}/assign-tutor`, {
    method: 'POST', body: JSON.stringify({ tutor_id: tutorId }),
  });

/** Payments */
export const fetchPayments = async (filters = {}) => {
  const qs = new URLSearchParams();
  if (filters.status) qs.append('status', filters.status);
  if (filters.startDate) qs.append('start_date', filters.startDate);
  if (filters.endDate) qs.append('end_date', filters.endDate);
  const q = qs.toString();
  return adminFetch(`/api/admin/payments${q ? `?${q}` : ''}`, { method: 'GET' });
};
export const fetchTutorPayouts = async () =>
  adminFetch('/api/admin/tutor-payouts', { method: 'GET' });

export const exportPaymentReport = async (filters = {}) => {
  const qs = new URLSearchParams(filters);
  const response = await fetch(`${BACKEND_URL}/api/admin/payments/export?${qs.toString()}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Export failed: HTTP ${response.status}`);
  return response.blob();
};
